import React, { useMemo, useState, useEffect, useCallback, useRef, useContext } from 'react';
import { m } from 'framer-motion';
import classNames from 'classnames';
import { useQuestionnaireContext, useQuestionnaireFormContext } from '@/components/Questionnaire/QuestionnaireProvider';
import generateValidationSchema from '@/helpers/validation/schema';
import { Questionnaire } from '@/typings/Questionnaire';
import Button from '@/components/DesignLibrary/Button';
import { useIsMobile, useToast } from '@/hooks';
import ProgressIndicator from '@components/ProgressIndicator';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import * as QuestionnaireApiModels from '@typings/questionnaires-api-models';
import TabText from '@components/DesignLibrary/Atoms/TabText';
import removeNullish from '@helpers/removeNullish';
import { useFeatureFlag } from '@providers/FeatureFlags';
import Spacer from '@components/Spacer';
import CheckCircle from '@components/Icons/CheckCircle';
import ToggleLeft from '@components/Icons/ToggleLeft';
import ToggleRight from '@components/Icons/ToggleRight';
import { useOpenDataReferencePane } from '@panes/dataReferenceHooks';
import FormPDFViewer from '@components/Questionnaire/form/FormPDFViewer';
import { DocumentAccessProvider } from '@providers/DocumentAccess';
import JsonModal from '@modals/JsonModal';
import trackEvents from '@/helpers/trackEvents';
import getListOrTableField from '@/helpers/getListOrTableField';
import Files from '@/components/Icons/Files';
import CaseDocumentsModal from '@/modals/CaseDocumentsModal';
import DashboardContext from '@/layouts/DashboardLayout/DashboardContext';

import FormField from '../FormField';
import SectionNavigation from '../SectionNavigation';
import SectionNavigationButton from '../SectionNavigationButton';
import AutofillExplanationModal from './AutofillExplanationModal';
import ExternalData from './ExternalData';
import HandleWatchers from '../HandleWatchers';
import SaveButton from './SaveButton';
import SubmitButton from './SubmitButton';
import s from './DynamicForm.module.scss';
import useFilteredSections from './useFilteredSections';
import formValuesToResponses from '../helpers/formValuesToResponses';

type Props = {
  formData: Questionnaire.Questionnaire;
  isCompleted: boolean;
  getFieldTemplatesForSectionTemplate: (section: Pick<QuestionnaireApiModels.SectionTemplate, 'id' | 'slug' | 'questionnaireTemplateId'>) => Promise<void>;
  shouldForceComplete?: boolean;
};

const FormContent: React.FC<Props> = ({ getFieldTemplatesForSectionTemplate, formData, isCompleted, shouldForceComplete }) => {
  trackEvents.track('render - FormContent');
  const {
    fieldsByKey,
    currentSection,
    setCurrentSection,
    userWorkflowId,
    fieldsById,
    onSubmit,
    isEnabled,
  } = useQuestionnaireContext();

  const {
    formMethods,
    setValue,
  } = useQuestionnaireFormContext();

  const { creator } = useContext(DashboardContext);

  const { control } = formMethods;
  const [selectedAssignee, setSelectedAssignee] = useState('All');
  const [caseDocumentsModalOpen, setCaseDocumentsModalOpen] = useState(false);
  const [currentSubsection, setCurrentSubsection] = useState<string | null>(null);
  const [showFillablePdf, setShowFillablePdf] = useState(false);
  const [showExternalData, setShowExternalData] = useState(false);
  const [pdfDocumentId, setPdfDocumentId] = useState<string | null>(null);
  const [pdfJson, setPdfJson] = useState<Record<string, unknown> | null>(null);
  const [pdfError, setPdfError] = useState<Record<string, unknown> | null>(null);
  const [isPdfJsonModalOpen, setIsPdfJsonModalOpen] = useState(false);
  const [isPdfErrorModalOpen, setIsPdfErrorModalOpen] = useState(false);
  const [isFetchingPdfDocument, setIsFetchingPdfDocument] = useState(false);
  const [fieldsAutofilling, setFieldsAutoFilling] = useState<string[]>([]);
  const [explanationModal,setExplanationModal] = useState<boolean>(false);
  const [fieldDataForExplanation,setFieldDataForExplanation] = useState<Questionnaire.Field>();
  const setDataReferenceId = useOpenDataReferencePane();
  const isMobile = useIsMobile();

  const toast = useToast();

  const {
    getResponseSource,
    getAiRequestId,
    getAutofillStatus,
    getExplanationHtml,
  } = useQuestionnaireFormContext();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;

  const handleSubmit = useCallback(async (): Promise<void> => {
    setIsSubmitting(true);
    const asResponses = formValuesToResponses(
      formData,
      getResponseSource,
      getAiRequestId,
      getAutofillStatus,
      getExplanationHtml,
      formData.sections,
      (fieldId: string) => Boolean(getListOrTableField({ fieldId, fieldsById })),
    );
    const success = await onSubmitRef.current(asResponses, true);
    if (success) {
      toast(useToast.ToastTypeVariants.SUCCESS, 'Responses submitted successfully!');
    }
    setIsSubmitting(false);
  }, [toast, fieldsById, getResponseSource, getAiRequestId, getAutofillStatus, formData]);

  useEffect(() => {
    if (shouldForceComplete) {
      handleSubmit();
    }
  }, [shouldForceComplete, formData, handleSubmit]);

  const { getData: generateFormPdfDocument } = useNoodleApi(tsClient.workflows.generateFormPDF);
  const { getData: getFormPdfDocumentId } = useNoodleApi(tsClient.workflows.getFormPDFDocumentId);

  const canUsePdfFillInsights = useFeatureFlag('pdf-fill-insights');
  const canShowExternalData = useFeatureFlag('questionnaire-show-external-data');

  const handleToggleShowFillablePdf = useCallback(() => setShowFillablePdf(prev => !prev), []);
  const handleToggleShowExternalData = useCallback(() => setShowExternalData(prev => !prev), []);

  useEffect(() => {
    if (currentSection) {
      window.scrollTo({
        top: 0,
      });
    }
  }, [currentSection]);

  const assignees = useMemo(() => formData.assigneeTemplates, [formData.assigneeTemplates]);

  const { currentSectionData, visibleSections, sectionsForNavigation } = useFilteredSections({
    allSections: formData.sections,
    selectedAssignee,
  });

  useEffect(() => {
    const getData = async (): Promise<void> => {
      if (currentSectionData?.pdfTemplateId && userWorkflowId) {
        const { data } = await getFormPdfDocumentId({
          pdfTemplateId: currentSectionData.pdfTemplateId,
          questionnaireId: formData.id,
          userWorkflowId,
        });
        if (data?.documentId) {
          setPdfDocumentId(data.documentId);
        }
      }
    };
    getData();
  }, [currentSectionData?.pdfTemplateId, userWorkflowId, formData.id, getFormPdfDocumentId]);

  useEffect(() => {
    setPdfJson(null);
    setPdfError(null);
  }, [currentSection]);

  useEffect(() => {
    if (currentSectionData && currentSectionData.isLoading) {
      getFieldTemplatesForSectionTemplate(currentSectionData);
    }
  }, [getFieldTemplatesForSectionTemplate, currentSectionData]);

  useEffect(() => {
    setCurrentSubsection(currentSectionData?.subsections?.[0] || null);
  }, [currentSectionData?.subsections]);

  const isLastSection = visibleSections.length > 0
    ? currentSection === visibleSections[visibleSections.length - 1].id
    : false;

  const isLastSubsection = currentSubsection !== null && currentSectionData?.subsections
    ? currentSubsection === currentSectionData.subsections[currentSectionData.subsections.length - 1]
    : false;

  const nextSectionIndex = visibleSections.findIndex(({ id }) => id === currentSection) + 1;

  const handleNextSection = (): void => {
    const currentSubsections = currentSectionData?.subsections || [];
    const currentSubsectionIndex = currentSubsections.findIndex(subsection => subsection === currentSubsection);

    const panes = document.getElementsByClassName('pane-content');

    if (panes.length === 0) {
      window.scrollTo({
        behavior: 'smooth',
        top: 0,
      });
    } else {
      const lastPaneElement = panes[panes.length - 1];
      if (lastPaneElement) {
        lastPaneElement.scrollTo({
          behavior: 'smooth',
          top: 0,
        });
      }
    }

    if (currentSubsectionIndex < currentSubsections.length - 1) {
      setCurrentSubsection(currentSubsections[currentSubsectionIndex + 1]);
    } else if (visibleSections[nextSectionIndex]) {
      setCurrentSection(visibleSections[nextSectionIndex].id);
    }
  };

  const validationSchema = useMemo(() =>
    generateValidationSchema(
      formData.sections.flatMap(section => section.fields),
    ), [formData.sections],
  );

  useEffect(() => {
    Object.values(fieldsByKey).forEach((field) => {
      const value = formMethods.watch(field.key);
      if ((field.type === 'list' || field.type === 'table') && !value) {
        setValue({ key: field.key, value: [] });
      }
    });
  }, [fieldsByKey, formMethods, setValue]);

  const showHeaderContent = userWorkflowId && !isMobile && !isCompleted;

  const showPdfButtons = currentSectionData?.pdfTemplateId && userWorkflowId && pdfDocumentId;

  useEffect(() => {
    if (!showHeaderContent) {
      setShowFillablePdf(false);
      setShowExternalData(false);
    }
  }, [showHeaderContent]);

  useEffect(() => {
    if (showFillablePdf) {
      setShowExternalData(false);
    }
  }, [showFillablePdf]);

  useEffect(() => {
    if (showExternalData) {
      setShowFillablePdf(false);
    }
  }, [showExternalData]);

  const explanationModalToggle = ():void => {
    setExplanationModal(!explanationModal);
  };

  const showSideNav = !showFillablePdf && !showExternalData && sectionsForNavigation.length > 1 && !isCompleted;

  return (
    <>
      <HandleWatchers
        isAutoSaveEnabled={isEnabled}
        isAutofillEnabled={isEnabled}
        isAiAgentsEnabled={isEnabled}
        isDynamicOptionsEnabled={true}
        isSectionsConditionEnabled={true}
        isActionsEnabled={isEnabled}
        formData={formData}
        onStartAgent={setFieldsAutoFilling}
      />
      {showHeaderContent && (
        <div className={s.headerContent}>
          <div className={s.buttons}>
            {!showSideNav && (
              <SectionNavigationButton
                sections={sectionsForNavigation}
                currentSection={currentSection}
                onSelectSection={setCurrentSection}
                completed={isCompleted}
              />
            )}
            {showPdfButtons && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleToggleShowFillablePdf}
              >
                {showFillablePdf
                  ? <ToggleRight width={20} height={20} weight='fill' color='var(--color-primary)' />
                  : <ToggleLeft width={20} height={20} weight='fill' color='var(--color-gray-50)' />}
                <span className={s.toggleButtonLabel}>Show Fillable PDFs</span>
              </Button>
            )}
            {canShowExternalData && <Button
              variant="secondary"
              size="sm"
              onClick={handleToggleShowExternalData}
            >
              {showExternalData
                ? <ToggleRight width={20} height={20} weight='fill' color='var(--color-primary)' />
                : <ToggleLeft width={20} height={20} weight='fill' color='var(--color-gray-50)' />}
              <span className={s.toggleButtonLabel}>Show External Data</span>
            </Button>}
          </div>
          <div className={s.buttons}>
            {showPdfButtons && (
              <>
                {showFillablePdf && pdfError && canUsePdfFillInsights && (
                  <Button
                    variant='secondary'
                    size='sm'
                    onClick={() => setIsPdfErrorModalOpen(true)}
                  >
                    Show PDF Error
                  </Button>
                )}
                {showFillablePdf && pdfJson && canUsePdfFillInsights && (
                  <Button
                    variant='secondary'
                    size='sm'
                    onClick={() => setIsPdfJsonModalOpen(true)}
                  >
                    Show JSON
                  </Button>
                )}
                {showFillablePdf && (
                  <Button
                    variant='primary'
                    size='sm'
                    loading={isFetchingPdfDocument}
                    onClick={async () => {
                      if (!currentSectionData?.pdfTemplateId) {
                        return;
                      }
                      setIsFetchingPdfDocument(true);
                      const { data } = await generateFormPdfDocument({
                        pdfTemplateId: currentSectionData.pdfTemplateId,
                        questionnaireId: formData.id,
                        userWorkflowId,
                      });
                      if (data?.documentId) {
                        setPdfDocumentId(data.documentId);
                      }
                      if (data?.json) {
                        setPdfJson(data.json);
                      }
                      if (data?.error) {
                        setPdfError(data.error);
                      }
                      setIsFetchingPdfDocument(false);
                    }}
                  >
                    Regenerate PDF
                  </Button>
                )}
              </>
            )}
            {userWorkflowId && <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setCaseDocumentsModalOpen(true);
              }}
            >
              <Files size={16} style={{ marginRight: 'var(--spacer-xs)' }} />
              <span>View case documents</span>
            </Button>}
          </div>
        </div>
      )}
      {!showSideNav && isCompleted && sectionsForNavigation.length > 0 && <>
        <Spacer />
        <SectionNavigationButton
          sections={sectionsForNavigation}
          currentSection={currentSection}
          onSelectSection={setCurrentSection}
          completed={isCompleted}
        />
      </>}
      <div className={classNames(s.formContainer, isCompleted && s.completed)}
        {...(!isEnabled && !isCompleted)
          ? {
            style: {
              alignItems: 'center',
              flexDirection: 'column',
            },
          }
          : {}
        }
      >
        {showSideNav && (
          <SectionNavigation
            sections={sectionsForNavigation}
            currentSection={currentSection}
            onSelectSection={setCurrentSection}
            completed={isCompleted}
            collapsed={!isEnabled}
            addStickyInset={Boolean(showHeaderContent)}
          />
        )}
        <div className={s.form}
          {...(!isEnabled || !showSideNav)
            ? {
              style: {
                borderLeft: 'none',
              },
            }
            : {}
          }
        >
          <div className={s.formContent}>
            {assignees.length > 0 && (
              <div className={s.filterContainer}>
                <button
                  className={selectedAssignee === 'All' ? s.activeFilter : ''}
                  onClick={() => setSelectedAssignee('All')}
                >
                  All
                </button>
                {assignees.map(assignee => (
                  <button
                    key={assignee.id}
                    className={
                      selectedAssignee === assignee.id ? s.activeFilter : ''
                    }
                    onClick={() => setSelectedAssignee(assignee.id)}
                  >
                    {assignee.label}
                  </button>
                ))}
              </div>
            )}
            {currentSectionData?.pdfTemplateId && userWorkflowId && pdfDocumentId && isCompleted && !isMobile && <>
              <Button
                className={s.completedShowPdf}
                variant="secondary"
                size="sm"
                onClick={() => setShowFillablePdf(prev => !prev)}
              >
                {showFillablePdf
                  ? <ToggleRight width={20} height={20} weight='fill' color='var(--color-primary)' />
                  : <ToggleLeft width={20} height={20} weight='fill' color='var(--color-gray-50)' />}
                <span style={{ marginLeft: 8 }}>Show PDF</span>
              </Button>
              <Spacer size={24} />
            </>}
            <h2 className='heading-sm'>{currentSectionData?.label}</h2>
            {currentSectionData?.description && <p className="body-sm">{currentSectionData?.description}</p>}
            {(currentSectionData?.subsections?.length || 0) > 1 && (
              <div className={s.tabs}>
                {[
                  ...(currentSectionData?.subsections || []),
                  ...(currentSectionData?.fields ?? []).filter((f) => !(currentSectionData?.subsections || []).includes(f.subsection || '')).map((f) => f.subsection),
                ].filter(removeNullish).map((subsection) => {
                  const progress = {
                    completedFields: 0,
                    subsectionProgressPercentage: null,
                    totalFields: 0,
                  };
                  return (
                    <TabText
                      key={subsection}
                      icon={progress?.subsectionProgressPercentage === 100 ? CheckCircle : undefined}
                      iconColor='var(--color-success)'
                      label={`${subsection}${progress?.subsectionProgressPercentage && progress.subsectionProgressPercentage < 100
                        ? ` (${progress.completedFields}/${progress.totalFields})`
                        : ''}`}
                      selected={currentSubsection === subsection}
                      onClick={() => setCurrentSubsection(subsection === currentSubsection ? null : subsection)}
                    />
                  );
                })}
              </div>
            )}
            {currentSectionData?.isLoading && (
              <>
                <ProgressIndicator isCentered />
                <Spacer size={32} />
              </>
            )}
            {(currentSectionData?.fields ?? [])
              .filter((field) => !field.listFieldTemplateId && (currentSubsection
                ? field.subsection === currentSubsection || field?.listItemTemplates?.find((f) => f.subsection === currentSubsection) || field.type === 'median-income'
                : true))
              .map(field => (
                <m.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} key={field.key} className={s.field}>
                  <FormField
                    field={
                      field.type === 'list' || field.type === 'table'
                        ? ({
                          ...field,
                          ...(isCompleted && { disabled: true }),
                          fields: (currentSectionData?.fields ?? [])
                            .filter(({ listFieldTemplateId, subsection }) =>
                              listFieldTemplateId === field.id && (currentSubsection ? subsection === currentSubsection : true),
                            ).map(f => ({
                              ...f,
                              disabled: isCompleted,
                            })),
                          listItemTemplates: (currentSectionData?.fields ?? [])
                            .filter(({ listFieldTemplateId }) => listFieldTemplateId === field.id)
                            .map(f => ({
                              ...f,
                              disabled: isCompleted,
                            }))
                            .sort((a, b) => a.rank - b.rank),
                        } as Questionnaire.FieldList)
                        : {
                          ...field,
                          ...(isCompleted && { disabled: true }),
                        }
                    }
                    control={control}
                    fieldsAutofilling={fieldsAutofilling}
                    rules={validationSchema[field.key]}
                    validationSchema={validationSchema}
                    onClickExplanation={(
                      field?.type==='median-income' && (() => {
                        setFieldDataForExplanation(field);
                        setExplanationModal(true);
                      }))
                      || (field.autofill?.expression?.variables[0]?.referenceType === 'data-reference-slug' && (() => {
                        setDataReferenceId({
                          dataReferenceId: field.autofill?.expression?.variables?.[0]?.referenceId || '',
                        });
                      }))
                      || undefined}
                  />
                </m.div>
              ))}
            {(isEnabled && !isCompleted) && (
              <m.div className={s.actions} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                key={currentSubsection || currentSection}>
                {isLastSection && (isLastSubsection || !currentSectionData?.subsections || currentSectionData?.subsections?.length === 0)
                  ? (
                    <SubmitButton
                      onClick={handleSubmit}
                      isSubmitting={isSubmitting}
                    />
                  )
                  : (
                    <Button variant="primary" size="md" onClick={handleNextSection}>
                      Next
                      {(currentSubsection && currentSectionData?.subsections
                        && currentSectionData.subsections.length > currentSectionData.subsections.indexOf(currentSubsection) + 1)
                        ? `: ${currentSubsection && currentSectionData.subsections[currentSectionData.subsections.indexOf(currentSubsection) + 1]}`
                        : (visibleSections[nextSectionIndex]?.label && `: ${visibleSections[nextSectionIndex]?.label}` || '')
                      }
                    </Button>
                  )}
                <SaveButton
                  formData={formData}
                />
              </m.div>
            )}
          </div>
        </div>
        {showFillablePdf && currentSectionData?.pdfTemplateId && userWorkflowId && pdfDocumentId && (
          <div className={classNames(s.rightPanel, isCompleted && s.pdfContainerCompleted)}>
            <DocumentAccessProvider>
              <FormPDFViewer documentId={pdfDocumentId} />
            </DocumentAccessProvider>
          </div>
        )}
        {showExternalData && (
          <div className={s.rightPanel} style={{ backgroundColor: 'var(--color-gray-15)' }}>
            <ExternalData />
          </div>
        )}
      </div>
      {isPdfJsonModalOpen && pdfJson && <JsonModal json={pdfJson} onClose={() => setIsPdfJsonModalOpen(false)} />}
      {isPdfErrorModalOpen && pdfError && <JsonModal json={pdfError} onClose={() => setIsPdfErrorModalOpen(false)} />}
      {explanationModal
       && fieldDataForExplanation?.id
       && <AutofillExplanationModal
         formData={formData}
         fieldData={fieldDataForExplanation}
         toggleModal={explanationModalToggle}
       />
      }
      {caseDocumentsModalOpen && userWorkflowId && creator && <CaseDocumentsModal
        setCaseDocumentsModalOpen={setCaseDocumentsModalOpen}
        creatorId={creator.id}
        userWorkflowId={userWorkflowId}
      />}
    </>
  );
};

export default FormContent;
